import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, VStack, HStack, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useCarousel } from '@/components/carousel/hooks/useCarousel';
import { PillButton } from '@/components/ui/PillButton';

import { markCarouselCompleted } from '../../services/carouselStorage';

const TOTAL_SLIDES = 7;
const RALEWAY_TITLE_SLIDES = [3, 4, 5, 6];

const MotionBox = motion.create(Box);

function CarouselSlide({ slideNum, t }: { slideNum: number; t: (key: string) => string }) {
  const title = t(`onboarding.carousel.slide${slideNum}Title`);
  const subtitle = t(`onboarding.carousel.slide${slideNum}Subtitle`);
  const useRaleway = RALEWAY_TITLE_SLIDES.includes(slideNum);

  return (
    <MotionBox
      key={slideNum}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
      display="flex"
      flexDirection="column"
      flex={1}
      justifyContent="center"
      alignItems="center"
      px={6}
      data-testid={`carousel-slide-${slideNum}`}
    >
      <Text
        fontFamily={useRaleway ? "'Raleway', sans-serif" : "'Inter', sans-serif"}
        fontWeight={useRaleway ? '200' : '700'}
        fontSize={useRaleway ? '47px' : '32px'}
        lineHeight={useRaleway ? '55px' : '40px'}
        textAlign="center"
        color="onboarding.cream"
        whiteSpace="pre-line"
      >
        {title}
      </Text>

      <Text
        fontFamily="'Inter', sans-serif"
        fontWeight="400"
        fontSize="16px"
        color="onboarding.mutedRose"
        textAlign="center"
        mt={4}
        lineHeight="24px"
        maxW="400px"
      >
        {subtitle}
      </Text>

      <Text fontFamily="'Inter', sans-serif" fontSize="14px" color="onboarding.placeholder" mt={6}>
        {slideNum} / {TOTAL_SLIDES}
      </Text>
    </MotionBox>
  );
}

export function OnboardingCarouselPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleComplete = useCallback(() => {
    markCarouselCompleted();
    navigate('/onboarding/wizard');
  }, [navigate]);

  const handleSkip = useCallback(() => {
    markCarouselCompleted();
    navigate('/onboarding/wizard');
  }, [navigate]);

  const carousel = useCarousel({
    totalSlides: TOTAL_SLIDES,
    onComplete: handleComplete,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (carousel.isLastSlide) handleComplete();
        else carousel.goToNext();
      } else if (e.key === 'ArrowLeft') {
        carousel.goToPrevious();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [carousel, handleComplete, handleSkip]);

  const slideNum = carousel.currentSlide + 1;
  const showSkip = carousel.currentSlide >= 1 && carousel.currentSlide <= 5;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      bg="onboarding.deepPlum"
      minH="100vh"
      position="relative"
      overflow="hidden"
      data-testid="carousel-screen"
    >
      {/* Decorative blobs */}
      <Box position="absolute" top={0} left={0} right={0} bottom={0} pointerEvents="none">
        <Box
          position="absolute"
          w="360px"
          h="320px"
          borderRadius="full"
          bg="onboarding.plum"
          opacity={0.4}
          top="10%"
          right="-10%"
          filter="blur(80px)"
        />
        <Box
          position="absolute"
          w="280px"
          h="240px"
          borderRadius="full"
          bg="onboarding.roseDeep"
          opacity={0.25}
          top="35%"
          left="-5%"
          filter="blur(60px)"
        />
        <Box
          position="absolute"
          w="200px"
          h="180px"
          borderRadius="full"
          bg="onboarding.lavender"
          opacity={0.1}
          bottom="20%"
          right="15%"
          filter="blur(50px)"
        />
      </Box>

      {/* Skip button */}
      {showSkip && (
        <Box position="absolute" top={4} right={4} zIndex={10}>
          <Text
            color="onboarding.mutedRose"
            fontSize="16px"
            fontWeight="500"
            cursor="pointer"
            onClick={handleSkip}
            _hover={{ opacity: 0.8 }}
            data-testid="carousel-skip-button"
          >
            {t('onboarding.carousel.skip')}
          </Text>
        </Box>
      )}

      {/* Slide content */}
      <Box flex={1} display="flex" justifyContent="center" zIndex={1}>
        <AnimatePresence mode="wait">
          <CarouselSlide slideNum={slideNum} t={t} />
        </AnimatePresence>
      </Box>

      {/* Navigation Footer */}
      <VStack px={6} pb={8} spacing={4} zIndex={1} align="center">
        {/* Progress Dots */}
        <HStack justify="center" align="center" spacing={2} py={4}>
          {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
            <Box
              key={i}
              h="8px"
              borderRadius="4px"
              cursor="pointer"
              bg={i === carousel.currentSlide ? 'onboarding.cream' : 'rgba(251, 245, 239, 0.3)'}
              w={i === carousel.currentSlide ? '24px' : '8px'}
              transition="all 0.2s"
              onClick={() => carousel.goToSlide(i)}
            />
          ))}
        </HStack>

        {/* Navigation Buttons */}
        <HStack justify="center" spacing={3} maxW="343px">
          {!carousel.isFirstSlide && (
            <PillButton
              label={t('onboarding.wizard.backButton')}
              variant="secondary"
              onPress={carousel.goToPrevious}
              testID="carousel-back-button"
            />
          )}
          <PillButton
            label={
              carousel.isLastSlide
                ? t('onboarding.carousel.getStarted')
                : t('onboarding.wizard.nextButton')
            }
            variant="primaryLight"
            onPress={carousel.isLastSlide ? handleComplete : carousel.goToNext}
            testID={carousel.isLastSlide ? 'carousel-start-button' : 'carousel-next-button'}
          />
        </HStack>
      </VStack>
    </Box>
  );
}

export default OnboardingCarouselPage;
